<template>
  <div>
    <global-confirm-box ref="confirmBox"></global-confirm-box>

    <page-header
      :title="$t('product_categories.title')"
      :subtitle="$t('product_categories.subtitle')"
      icon="mdi-shape-outline"
      icon-color="primary"
    >
      <template #actions>
        <primary-button @click="openDialog(null, null)">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("product_categories.add") }}
        </primary-button>
      </template>
    </page-header>

    <v-dialog v-model="dialog" max-width="460">
      <v-card>
        <v-card-title>{{ form.id ? $t("product_categories.edit") : $t("product_categories.add") }}</v-card-title>
        <v-card-text>
          <v-form v-model="valid" @submit.prevent="save">
            <text-field v-model="form.name" :label="$t('title')" :rules="[rules.required]" autofocus />
            <autocomplete
              v-model="form.parent_id"
              :items="parentOptions"
              :label="$t('product_categories.parent')"
              item-value="id"
              item-text="path"
              clearable
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">{{ $t("cancel") }}</v-btn>
          <primary-button :disabled="!valid || saving" @click="save">{{ $t("save") }}</primary-button>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="px-6 mt-4">
      <v-progress-linear v-if="loading" indeterminate />
      <div v-else-if="!rows.length" class="text-center grey--text py-12">{{ $t("product_categories.empty") }}</div>
      <v-card v-else class="ordera-box-shadow">
        <v-list dense>
          <v-list-item v-for="row in rows" :key="row.id" :style="{ paddingLeft: 16 + row.depth * 28 + 'px' }">
            <v-icon small class="mr-2" color="grey">{{ row.hasChildren ? "mdi-folder-outline" : "mdi-tag-outline" }}</v-icon>
            <v-list-item-content>
              <v-list-item-title :class="{ 'font-weight-medium': !row.depth }">{{ row.name }}</v-list-item-title>
              <v-list-item-subtitle>
                {{ $t("product_categories.counts", { g: row.groceries_count, s: row.suppliers_count }) }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action class="flex-row">
              <v-btn icon small :title="$t('product_categories.add_sub')" @click="openDialog(null, row.id)">
                <v-icon small>mdi-plus</v-icon>
              </v-btn>
              <edit-button class="mr-2" @click="openDialog(row)" />
              <remove-button @click="remove(row)" />
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card>
    </div>
  </div>
</template>

<script>
import PageHeader from "@/components/global/PageHeader.vue";
import hasValidation from "@/mixins/hasValidation";
import hasSnackbar from "@/mixins/hasSnackbar";
import productCategoryService from "@/services/productCategory";

/** Admin: jedinstvene kategorije namirnica/proizvoda (stablo sa podkategorijama). */
export default {
  name: "ProductCategories",
  mixins: [hasValidation, hasSnackbar],
  components: { PageHeader },
  data: () => ({
    tree: [],
    loading: false,
    dialog: false,
    valid: false,
    saving: false,
    form: { id: null, name: "", parent_id: null },
  }),
  computed: {
    /** Ravna lista u redosledu stabla (sa dubinom) za prikaz. */
    rows() {
      const walk = (nodes, depth) =>
        nodes.reduce(
          (list, node) =>
            list.concat([{ ...node, depth, hasChildren: !!(node.children && node.children.length) }], walk(node.children || [], depth + 1)),
          []
        );
      return walk(this.tree, 0);
    },
    /** Moguće nadkategorije: sve osim same kategorije i njenih potomaka. */
    parentOptions() {
      const blocked = new Set();
      if (this.form.id) {
        const collect = (nodes) =>
          nodes.forEach((node) => {
            if (node.id === this.form.id || blocked.has(node.parent_id)) blocked.add(node.id);
            collect(node.children || []);
          });
        // dvaput da obuhvati potomke nezavisno od redosleda
        collect(this.tree);
        collect(this.tree);
      }
      const build = (nodes, prefix) =>
        nodes.reduce((list, node) => {
          const path = prefix ? `${prefix} › ${node.name}` : node.name;
          return blocked.has(node.id) ? list : list.concat([{ id: node.id, path }], build(node.children || [], path));
        }, []);
      return build(this.tree, "");
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        this.tree = await productCategoryService.getTree(true);
      } finally {
        this.loading = false;
      }
    },
    openDialog(row, parentId) {
      this.form = row
        ? { id: row.id, name: row.name, parent_id: row.parent_id }
        : { id: null, name: "", parent_id: parentId || null };
      this.dialog = true;
    },
    async save() {
      this.saving = true;
      try {
        const payload = { name: this.form.name.trim(), parent_id: this.form.parent_id || null };
        if (this.form.id) {
          await productCategoryService.update(this.form.id, payload);
        } else {
          await productCategoryService.create(payload);
        }
        this.dialog = false;
        await this.load();
      } catch (e) {
        this.showError(e);
      } finally {
        this.saving = false;
      }
    },
    remove(row) {
      this.$refs.confirmBox
        .open(this.$t("delete_confirmation"), this.$t("product_categories.delete_text", { name: row.name }), { color: "error" })
        .then(async () => {
          try {
            await productCategoryService.remove(row.id);
            await this.load();
          } catch (e) {
            this.showError(e);
          }
        })
        .catch(() => {});
    },
    showError(e) {
      const data = e.response && e.response.data;
      const first = data && data.errors && Object.values(data.errors)[0];
      this.$store.dispatch("snackbar/showSnackbar", {
        message: (first && first[0]) || (data && data.message) || this.$t("product_categories.error"),
        color: "error",
      });
    },
  },
};
</script>
